import { ParsedUrlQuery, stringify } from 'querystring';

import { CurrentNetworkContext, SearchLocationParameters, Thread } from '@ms/yammer-data/dist/state/types';

import { GetBaseUrlProps, getBaseUrl } from '../location';

import { groupPage } from './groups';
import { userPage } from './users';

import { ThreadPageQueryOptions, campaignUrl, makeSearchQueryStringParameters, rootPageUrl } from '.';

export const articlePageMatchPath = '/articles/:threadId';
export const articleEditPageMatchPath = '/articles/:threadId/edit';
export const articleNewMatchPath = '/articles/new';

export const articleBasePath = '/articles';
export const articleNewBasePath = '/articles/new';

export interface PageArticleMatchParams {
  readonly threadId: Thread['id'];
}

export type ArticlePageQueryOptions = ThreadPageQueryOptions;

const getLocationSearchQueryString = (queryOptions?: ArticlePageQueryOptions) =>
  queryOptions ? `?${stringify(queryOptions as unknown as ParsedUrlQuery)}` : undefined;

type ArticlePage = (articleId: Thread['id'], queryOptions?: ArticlePageQueryOptions) => string;
export const articlePage: ArticlePage = (articleId, queryOptions) => {
  const queryString = getLocationSearchQueryString(queryOptions) ?? '';

  return `${articleBasePath}/${articleId}${queryString}`;
};

interface ArticlePageNewUrlParams {
  readonly userId?: string;
  readonly groupId?: string | null;
  readonly campaignId?: string;
}

type ArticlePageNewUrl = (params: ArticlePageNewUrlParams) => string;
export const articlePageNewUrl: ArticlePageNewUrl = ({ userId, groupId, campaignId }) => {
  const params: Record<string, string> = {};

  if (groupId) {
    params['groupId'] = groupId;
  }

  if (userId) {
    params['userId'] = userId;
  }

  if (campaignId) {
    params['campaignId'] = campaignId;
  }

  const keys = Object.keys(params);
  const pathWithParams = keys.length > 0 ? `${articleNewBasePath}?${stringify(params)}` : articleNewBasePath;

  return pathWithParams;
};

type ArticlePageEditUrl = (threadId: Thread['id']) => string;
export const articlePageEditUrl: ArticlePageEditUrl = (threadId) => `${articleBasePath}/${threadId}/edit`;

type OnArticlePublishRedirectPath = (params: ArticlePageNewUrlParams) => string;
export const onArticlePublishRedirectPath: OnArticlePublishRedirectPath = ({ userId, groupId, campaignId }) => {
  if (groupId) {
    return groupPage(groupId);
  }

  if (userId) {
    return userPage(userId);
  }

  if (campaignId) {
    return campaignUrl(campaignId);
  }

  return rootPageUrl;
};

interface articleUrlProps {
  readonly threadId: string;
  readonly options?: ThreadPageQueryOptions;
  readonly currentNetworkContext?: CurrentNetworkContext;
  readonly host?: GetBaseUrlProps['host'];
}
type ArticleUrl = (props: articleUrlProps) => string;
export const articleUrl: ArticleUrl = ({ threadId, options, currentNetworkContext, host }) => {
  const baseUrl = getBaseUrl({ currentNetworkContext, host });

  return `${baseUrl}${articlePage(threadId, options)}`;
};

type ArticlePageWithSearchParameters = (id: Thread['id'], searchParams: SearchLocationParameters) => string;
export const articlePageWithSearchParameters: ArticlePageWithSearchParameters = (id, searchParams) => {
  const queryOptions = makeSearchQueryStringParameters(searchParams);

  return articlePage(id, queryOptions);
};
